import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert,
} from "@mui/material";
import { PersonOutline as PersonIcon } from "@mui/icons-material";
import {
  NAVY, cardSx, headCellSx, EmployeeSelect, CURRENT_YEAR, getYearOptions,
} from "../../../components/leave/LeaveCommon";
import { fetchLeaveAllocation, clearLeaveAllocation } from "../../../actions/Leave/LeaveAllocationAction";
import { toEmployeeOptions } from "../../../service/Leave/leaveHelpers";

const ROWS = 7;
const f2 = (n) => Number(n).toFixed(2);

const LeaveAllocationTab = () => {
  const dispatch = useDispatch();
  const { data: rawEmployees } = useSelector((s) => s.getAllEmployees) || {};
  const { data: leaveTypes = [] } = useSelector((s) => s.leaveTypes) || {};
  const { data: allocations = [], loading, error } = useSelector((s) => s.leaveAllocations) || {};

  const [employee, setEmployee] = useState(null);
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [page, setPage] = useState(0);

  const employees = useMemo(() => toEmployeeOptions(rawEmployees), [rawEmployees]);
  const typeNameById = useMemo(() => Object.fromEntries(leaveTypes.map((t) => [t.id, t.name])), [leaveTypes]);

  // the allocation call only runs when an employee is picked
  useEffect(() => {
    if (employee) dispatch(fetchLeaveAllocation(employee.serviceNo));
    else dispatch(clearLeaveAllocation());
  }, [employee, dispatch]);

  const rows = useMemo(
    () =>
      allocations
        .filter((a) => a.year === year)
        .map((a) => ({ ...a, leaveType: a.typeName || typeNameById[a.typeId] || a.typeCode || "—" })),
    [allocations, year, typeNameById]
  );

  const totals = useMemo(
    () => rows.reduce(
      (t, r) => ({
        allocated: t.allocated + r.allocated, adjustment: t.adjustment + r.adjustment,
        used: t.used + r.used, balance: t.balance + r.balance,
      }),
      { allocated: 0, adjustment: 0, used: 0, balance: 0 }
    ),
    [rows]
  );

  const pageRows = rows.slice(page * ROWS, page * ROWS + ROWS);
  const cols = 5;

  return (
    <Paper elevation={0} sx={{ ...cardSx, overflow: "hidden" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, px: 2.5, py: 2, borderBottom: "1px solid #eef0f4" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>Select Employee:</Typography>
        <EmployeeSelect
          label="Employee" options={employees} value={employee}
          onChange={(e) => { setEmployee(e); setPage(0); }}
          sx={{ width: 340 }}
        />
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Year</InputLabel>
          <Select value={year} label="Year" onChange={(e) => { setYear(e.target.value); setPage(0); }}>
            {getYearOptions().map((y) => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        {employee && (
          <Typography sx={{ ml: "auto", fontSize: 13, color: "#64748b" }}>
            <b style={{ color: "#334155" }}>Selected:</b> {employee.name}{" "}
            <span style={{ color: "#94a3b8", fontSize: 12 }}>({employee.serviceNo})</span>
          </Typography>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

      <TableContainer>
        <Table size="small" sx={{ minWidth: 740 }}>
          <TableHead>
            <TableRow>
              {["Leave Type", "Allocated", "Adjustments", "Used", "Balance"].map((h) => (
                <TableCell key={h} sx={headCellSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!employee && (
              <TableRow>
                <TableCell colSpan={cols} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                  <PersonIcon sx={{ display: "block", mx: "auto", mb: 0.5, color: "#cbd5e1" }} />
                  Please select an employee to view leave allocation
                </TableCell>
              </TableRow>
            )}
            {employee && loading && (
              <TableRow>
                <TableCell colSpan={cols} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell>
              </TableRow>
            )}
            {employee && !loading && !error && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={cols} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                  No leave allocation found for {employee.name} in {year}.
                </TableCell>
              </TableRow>
            )}
            {pageRows.map((r) => (
              <TableRow key={r.key} hover>
                <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{r.leaveType}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{f2(r.allocated)}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{f2(r.adjustment)}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{f2(r.used)}</TableCell>
                <TableCell sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{f2(r.balance)}</TableCell>
              </TableRow>
            ))}
            {employee && !loading && rows.length > 0 && (
              <TableRow sx={{ "& td": { borderTop: "2px solid #cbd5e1", fontWeight: 700 } }}>
                <TableCell sx={{ fontSize: 13 }}>Totals</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{f2(totals.allocated)}</TableCell>
                <TableCell sx={{ fontSize: 13, color: "#64748b" }}>{f2(totals.adjustment)}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{f2(totals.used)}</TableCell>
                <TableCell sx={{ fontSize: 16, color: NAVY }}>{f2(totals.balance)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {employee && rows.length > ROWS && (
        <TablePagination
          component="div" count={rows.length} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={ROWS} rowsPerPageOptions={[ROWS]}
        />
      )}
    </Paper>
  );
};

export default LeaveAllocationTab;